import type {
  DecodeMode,
  FileMeta,
  RadiometricSettings,
  Thermogram
} from "./types";

const COMMON_THERMAL_SIZES: Array<[number, number]> = [
  [80, 60],
  [120, 90],
  [160, 120],
  [256, 192],
  [320, 240],
  [384, 288],
  [464, 348],
  [640, 480]
];

const defaultFileMeta = (file: File): FileMeta => ({
  name: file.name,
  size: file.size,
  lastModified: file.lastModified,
  type: file.type
});

const bytesToDataUrl = (bytes: Uint8Array, mimeType: string) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return `data:${mimeType};base64,${btoa(binary)}`;
};

const readUint32Le = (bytes: Uint8Array, offset: number) =>
  bytes[offset] |
  (bytes[offset + 1] << 8) |
  (bytes[offset + 2] << 16) |
  (bytes[offset + 3] << 24);

const readUint16Le = (bytes: Uint8Array, offset: number) =>
  bytes[offset] | (bytes[offset + 1] << 8);

const readInt32Le = (bytes: Uint8Array, offset: number) => readUint32Le(bytes, offset) | 0;

const findJpegEnd = (bytes: Uint8Array, limit: number) => {
  for (let index = 2; index < limit - 1; index += 1) {
    if (bytes[index] === 0xff && bytes[index + 1] === 0xd9) {
      return index + 2;
    }
  }
  return limit;
};

const bmpPayloadEnd = (bytes: Uint8Array, limit: number) => {
  if (bytes.length < 54) return limit;
  const pixelOffset = readUint32Le(bytes, 10);
  const width = Math.abs(readInt32Le(bytes, 18));
  const height = Math.abs(readInt32Le(bytes, 22));
  const bitsPerPixel = readUint16Le(bytes, 28);
  const imageSize = readUint32Le(bytes, 34);
  const rowSize = Math.floor((bitsPerPixel * width + 31) / 32) * 4;
  const pixelBytes = imageSize || rowSize * height;
  const end = pixelOffset + pixelBytes;
  return end > pixelOffset && end <= limit ? end : limit;
};

const embeddedPreviewUrl = (bytes: Uint8Array, limit: number) => {
  if (limit < 64) return undefined;
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return bytesToDataUrl(bytes.subarray(0, findJpegEnd(bytes, limit)), "image/jpeg");
  }
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return bytesToDataUrl(bytes.subarray(0, bmpPayloadEnd(bytes, limit)), "image/bmp");
  }
  return undefined;
};

const roundTo = (value: number, digits: number) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

// Integrated blackbody radiance over the uncooled detector band (8-14 um),
// in arbitrary units. A microbolometer responds ~linearly to incident band
// radiance, while radiance is non-linear in temperature (Planck's law). We use
// this to interpolate between the two calibrated scaling endpoints with the
// correct radiometric curvature instead of a straight line in raw counts.
const planckBandRadiance = (tempK: number) => {
  const h = 6.62607015e-34;
  const c = 299792458;
  const kB = 1.380649e-23;
  const lambda1 = 8e-6;
  const lambda2 = 14e-6;
  const steps = 32;
  const dLambda = (lambda2 - lambda1) / steps;
  let radiance = 0;
  for (let step = 0; step < steps; step += 1) {
    const lambda = lambda1 + (step + 0.5) * dLambda;
    const exponent = (h * c) / (lambda * kB * tempK);
    radiance += ((2 * h * c * c) / lambda ** 5 / (Math.exp(exponent) - 1)) * dLambda;
  }
  return radiance;
};

// Maps raw counts to temperatures by anchoring on the two calibrated endpoints
// (rawMin -> minK, rawMax -> maxK) and interpolating linearly in band radiance.
// Endpoints stay exact; intermediate values follow the Planck curve, which
// removes the few-degrees error a straight raw-to-temperature line introduces
// over wide thermal spans.
const mapRawToCelsiusRadiometric = (
  target: Float32Array,
  rawValues: Uint16Array,
  rawMin: number,
  rawMax: number,
  minK: number,
  maxK: number
) => {
  const sampleCount = 256;
  const tempSamples = new Float64Array(sampleCount);
  const radSamples = new Float64Array(sampleCount);
  for (let index = 0; index < sampleCount; index += 1) {
    const t = minK + ((maxK - minK) * index) / (sampleCount - 1);
    tempSamples[index] = t;
    radSamples[index] = planckBandRadiance(t);
  }
  const radMin = radSamples[0];
  const radMax = radSamples[sampleCount - 1];
  const rawSpan = Math.max(1, rawMax - rawMin);
  for (let index = 0; index < rawValues.length; index += 1) {
    const fraction = (rawValues[index] - rawMin) / rawSpan;
    const targetRad = radMin + fraction * (radMax - radMin);
    let lo = 0;
    let hi = sampleCount - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (radSamples[mid] <= targetRad) lo = mid;
      else hi = mid;
    }
    const span = radSamples[hi] - radSamples[lo] || 1;
    const weight = (targetRad - radSamples[lo]) / span;
    target[index] = tempSamples[lo] + weight * (tempSamples[hi] - tempSamples[lo]) - 273.15;
  }
};

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(String(reader.result)));
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(blob);
  });

export const createSyntheticThermogram = (): Thermogram => {
  const width = 256;
  const height = 192;
  const values = new Float32Array(width * height);
  const gaussians = [
    { x: 0.66, y: 0.36, temp: 31, radius: 0.11 },
    { x: 0.56, y: 0.53, temp: 24, radius: 0.09 },
    { x: 0.34, y: 0.47, temp: 13, radius: 0.12 },
    { x: 0.74, y: 0.68, temp: 18, radius: 0.12 }
  ];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const nx = x / (width - 1);
      const ny = y / (height - 1);
      let temp = 27 + nx * 5 - ny * 1.8 + Math.sin(nx * 18) * 0.7;
      if (x > width * 0.2 && x < width * 0.83 && y > height * 0.2 && y < height * 0.78) {
        temp += 7.5;
      }
      if (x > width * 0.18 && x < width * 0.84 && y > height * 0.32 && y < height * 0.39) {
        temp += 9;
      }
      if (x > width * 0.18 && x < width * 0.84 && y > height * 0.6 && y < height * 0.67) {
        temp += 7;
      }
      for (const point of gaussians) {
        const dx = nx - point.x;
        const dy = ny - point.y;
        temp += point.temp * Math.exp(-(dx * dx + dy * dy) / point.radius ** 2);
      }
      if (x > width * 0.44 && x < width * 0.49 && y > height * 0.22 && y < height * 0.77) {
        temp += 11;
      }
      if (x > width * 0.63 && x < width * 0.68 && y > height * 0.18 && y < height * 0.72) {
        temp += 15;
      }
      values[y * width + x] = temp;
    }
  }

  return {
    width,
    height,
    values,
    decodeMode: "synthetic",
    confidence: 1,
    unit: "celsius",
    note: "Thermogramme de demonstration charge au demarrage."
  };
};

export const valueStats = (values: Float32Array) => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let sum = 0;
  for (const value of values) {
    if (!Number.isFinite(value)) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
    sum += value;
  }
  return {
    min,
    max,
    avg: sum / Math.max(1, values.length)
  };
};

export const applyRadiometricCorrection = (
  values: Float32Array,
  settings: RadiometricSettings,
  sourceSettings?: Partial<RadiometricSettings>
) => {
  const targetEmissivity = Math.min(1, Math.max(0.05, settings.emissivity));
  const sourceEmissivity = sourceSettings?.emissivity
    ? Math.min(1, Math.max(0.05, sourceSettings.emissivity))
    : 1;
  const sourceReflectedTempC =
    sourceSettings?.reflectedTempC ?? settings.reflectedTempC;

  if (
    Math.abs(targetEmissivity - sourceEmissivity) < 0.0001 &&
    Math.abs(settings.reflectedTempC - sourceReflectedTempC) < 0.0001
  ) {
    return values;
  }

  if (targetEmissivity > 0.995 && sourceEmissivity > 0.995) {
    return values;
  }

  const sourceReflectedK = sourceReflectedTempC + 273.15;
  const reflectedK = settings.reflectedTempC + 273.15;
  const corrected = new Float32Array(values.length);
  for (let index = 0; index < values.length; index += 1) {
    const sourceObjectK = values[index] + 273.15;
    const apparentK =
      sourceEmissivity * sourceObjectK ** 4 +
      (1 - sourceEmissivity) * sourceReflectedK ** 4;
    const numerator =
      apparentK - (1 - targetEmissivity) * reflectedK ** 4;
    const objectK = Math.max(0, numerator / targetEmissivity) ** 0.25;
    corrected[index] = objectK - 273.15;
  }
  return corrected;
};

export const decodeThermogramFile = async (file: File): Promise<Thermogram> => {
  const buffer = await file.arrayBuffer();
  const meta = defaultFileMeta(file);

  const tofoMatrix = parseToFoRadiometric(buffer, meta);
  if (tofoMatrix) return tofoMatrix;

  const textMatrix = parseTextMatrix(buffer, meta);
  if (textMatrix) return textMatrix;

  const imageMatrix = await parseImageMatrix(file, meta);
  if (imageMatrix) return imageMatrix;

  const binaryMatrix = parseBinaryRadiometric(buffer, meta);
  if (binaryMatrix) return binaryMatrix;

  return parseBinaryPreview(buffer, meta);
};

type ToFoItem = {
  path: string;
  type: string;
  size: number;
  offset: number;
};

const parseToFoRadiometric = (
  buffer: ArrayBuffer,
  file: FileMeta
): Thermogram | null => {
  const bytes = new Uint8Array(buffer);
  const text = new TextDecoder("latin1").decode(bytes);
  const headerMatch = text.match(
    /<xml size="(\d+)" \/>\s*<data size="(\d+)" endianness="little" \/>\s*<\/ToFo>\s*/
  );
  if (!headerMatch || headerMatch.index === undefined) return null;

  const xmlSize = Number.parseInt(headerMatch[1], 10);
  const dataSize = Number.parseInt(headerMatch[2], 10);
  const xmlStart = headerMatch.index + headerMatch[0].length;
  const dataStart = xmlStart + xmlSize;
  if (
    !Number.isFinite(xmlSize) ||
    !Number.isFinite(dataSize) ||
    xmlStart < 0 ||
    dataStart + dataSize > bytes.length + 32
  ) {
    return null;
  }

  const xml = text.slice(xmlStart, xmlStart + xmlSize);
  const items = parseToFoItems(xml);
  if (items.length === 0) return null;
  const itemByPath = new Map(items.map((item) => [item.path, item]));
  const view = new DataView(buffer);
  const visualUrl = embeddedPreviewUrl(bytes, headerMatch.index);

  const readRaw = (path: string) => {
    const item = itemByPath.get(path);
    if (!item || dataStart + item.offset + item.size > bytes.length) return null;
    return {
      item,
      offset: dataStart + item.offset
    };
  };

  const readFloat = (path: string) => {
    const raw = readRaw(path);
    if (!raw || raw.item.size < 4) return undefined;
    return view.getFloat32(raw.offset, true);
  };

  const readUint16 = (path: string) => {
    const raw = readRaw(path);
    if (!raw || raw.item.size < 2) return undefined;
    return view.getUint16(raw.offset, true);
  };

  const irRaw = readRaw("BmtMetaData.Images.Ir");
  if (!irRaw || irRaw.item.size < 32) return null;

  const cvType = view.getUint32(irRaw.offset, true);
  const rowsFromHeader = view.getUint32(irRaw.offset + 4, true);
  const colsFromHeader = view.getUint32(irRaw.offset + 8, true);
  const bytesPerPixel = view.getUint32(irRaw.offset + 12, true);
  const rows = readUint16("BmtMetaData.IrImage.Rows") ?? rowsFromHeader;
  const cols = readUint16("BmtMetaData.IrImage.Cols") ?? colsFromHeader;
  const payloadOffset = irRaw.offset + 24;
  const pixelCount = rows * cols;

  if (
    cvType !== 2 ||
    bytesPerPixel !== 2 ||
    rows < 8 ||
    cols < 8 ||
    pixelCount * 2 > irRaw.item.size - 24
  ) {
    return null;
  }

  const rawValues = new Uint16Array(pixelCount);
  let rawMin = Number.POSITIVE_INFINITY;
  let rawMax = Number.NEGATIVE_INFINITY;
  for (let index = 0; index < pixelCount; index += 1) {
    const raw = view.getUint16(payloadOffset + index * 2, true);
    rawValues[index] = raw;
    rawMin = Math.min(rawMin, raw);
    rawMax = Math.max(rawMax, raw);
  }

  const scalingMinK = readFloat("BmtMetaData.Scaling.TempMin");
  const scalingMaxK = readFloat("BmtMetaData.Scaling.TempMax");
  const hasScaling =
    Number.isFinite(scalingMinK) &&
    Number.isFinite(scalingMaxK) &&
    rawMax > rawMin &&
    (scalingMaxK as number) > (scalingMinK as number);

  const values = new Float32Array(pixelCount);
  if (hasScaling) {
    mapRawToCelsiusRadiometric(
      values,
      rawValues,
      rawMin,
      rawMax,
      scalingMinK as number,
      scalingMaxK as number
    );
  } else {
    for (let index = 0; index < pixelCount; index += 1) {
      values[index] = rawValues[index] / 100;
    }
  }

  const emissivity = readFloat("BmtMetaData.Environment.EmissivityValue");
  const reflectedK = readFloat("BmtMetaData.Environment.ReflectedTemperature");
  const ambientK = readFloat("BmtMetaData.Environment.Temperature");
  const humidity = readFloat("BmtMetaData.Environment.Humidity");

  const defaults: Partial<RadiometricSettings> = {};
  if (emissivity && emissivity > 0 && emissivity <= 1) {
    defaults.emissivity = roundTo(emissivity, 2);
  }
  if (reflectedK && reflectedK > 100) {
    defaults.reflectedTempC = roundTo(reflectedK - 273.15, 1);
  }
  if (ambientK && ambientK > 100) {
    defaults.ambientTempC = roundTo(ambientK - 273.15, 1);
  }
  if (humidity !== undefined && Number.isFinite(humidity)) {
    defaults.humidityPct = roundTo(humidity <= 1 ? humidity * 100 : humidity, 1);
  }

  return {
    width: cols,
    height: rows,
    values,
    visualUrl,
    visualSource: visualUrl ? "embedded-preview" : undefined,
    decodeMode: "tofo-radiometric",
    confidence: hasScaling ? 0.86 : 0.72,
    unit: "celsius",
    note: visualUrl
      ? "Affichage thermique embarque du .BMT utilise pour restituer toute la scene. Les mesures restent calculees sur la matrice ToFo Ir."
      : hasScaling
        ? "Matrice ToFo Ir extraite du fichier .BMT. Les valeurs brutes sont mappees sur Scaling.TempMin/TempMax avec interpolation radiometrique (loi de Planck, bande 8-14 um)."
        : "Matrice ToFo Ir extraite du fichier .BMT. Conversion thermique precise a confirmer avec les coefficients fabricant.",
    file,
    rawRange: {
      min: rawMin,
      max: rawMax,
      scale: hasScaling ? "tofo_scaling_temp_min_max" : "tofo_raw_uint16_div100",
      offset: payloadOffset
    },
    radiometricDefaults: defaults
  };
};

const parseToFoItems = (xml: string): ToFoItem[] => {
  const tagPattern =
    /<group name="([^"]+)">|<\/group>|<item name="([^"]+)" type="([^"]+)" size="(\d+)" \/>/g;
  const stack: string[] = [];
  const items: ToFoItem[] = [];
  let relativeOffset = 0;
  for (const match of xml.matchAll(tagPattern)) {
    if (match[0] === "</group>") {
      stack.pop();
    } else if (match[1]) {
      stack.push(match[1]);
    } else if (match[2]) {
      const size = Number.parseInt(match[4], 10);
      const item: ToFoItem = {
        path: [...stack, match[2]].join("."),
        type: match[3],
        size,
        offset: relativeOffset
      };
      items.push(item);
      relativeOffset += size;
    }
  }
  return items;
};

const parseTextMatrix = (buffer: ArrayBuffer, file: FileMeta): Thermogram | null => {
  const bytes = new Uint8Array(buffer);
  const sampleLength = Math.min(bytes.length, 8192);
  let printable = 0;
  for (let index = 0; index < sampleLength; index += 1) {
    const byte = bytes[index];
    if (
      byte === 9 ||
      byte === 10 ||
      byte === 13 ||
      (byte >= 32 && byte <= 126) ||
      byte >= 160
    ) {
      printable += 1;
    }
  }
  if (sampleLength === 0 || printable / sampleLength < 0.72) return null;

  const text = new TextDecoder("utf-8").decode(buffer);
  const rows = text
    .split(/\r?\n/)
    .map((line) => {
      const matches = line.match(/[-+]?\d+(?:[.,]\d+)?/g) ?? [];
      return matches.map((token) => Number.parseFloat(token.replace(",", ".")));
    })
    .filter((row) => row.length >= 4 && row.every(Number.isFinite));

  if (rows.length < 4) return null;

  const widthCounts = new Map<number, number>();
  for (const row of rows) {
    widthCounts.set(row.length, (widthCounts.get(row.length) ?? 0) + 1);
  }
  const [width] = [...widthCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  const normalizedRows = rows.filter((row) => row.length === width);
  if (normalizedRows.length < 4 || width < 4) return null;

  const height = normalizedRows.length;
  const values = new Float32Array(width * height);
  normalizedRows.forEach((row, y) => {
    row.forEach((value, x) => {
      values[y * width + x] = value;
    });
  });

  return {
    width,
    height,
    values,
    decodeMode: "text-matrix",
    confidence: 0.95,
    unit: "celsius",
    note: "Matrice texte/CSV reconnue. Les valeurs sont lues comme des degres Celsius.",
    file
  };
};

const parseImageMatrix = async (
  file: File,
  meta: FileMeta
): Promise<Thermogram | null> => {
  const lowerName = file.name.toLowerCase();
  const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const isJpeg = header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;
  const isBmp = header[0] === 0x42 && header[1] === 0x4d;
  const looksLikeImage =
    file.type.startsWith("image/") ||
    lowerName.endsWith(".png") ||
    lowerName.endsWith(".jpg") ||
    lowerName.endsWith(".jpeg") ||
    lowerName.endsWith(".webp") ||
    isJpeg ||
    isBmp;
  if (!looksLikeImage) return null;

  try {
    const bitmap = await createImageBitmap(file);
    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / bitmap.width);
    const width = Math.max(8, Math.round(bitmap.width * scale));
    const height = Math.max(8, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const data = ctx.getImageData(0, 0, width, height).data;
    const values = new Float32Array(width * height);
    for (let index = 0; index < values.length; index += 1) {
      const offset = index * 4;
      const luminance =
        data[offset] * 0.2126 + data[offset + 1] * 0.7152 + data[offset + 2] * 0.0722;
      values[index] = 18 + (luminance / 255) * 62;
    }
    const visualBytes = new Uint8Array(await file.arrayBuffer());
    const visualUrl = isJpeg
      ? bytesToDataUrl(
          visualBytes.subarray(0, findJpegEnd(visualBytes, visualBytes.length)),
          "image/jpeg"
        )
      : isBmp
        ? bytesToDataUrl(
            visualBytes.subarray(0, bmpPayloadEnd(visualBytes, visualBytes.length)),
            "image/bmp"
          )
        : await blobToDataUrl(file);
    return {
      width,
      height,
      values,
      visualUrl,
      visualSource: "imported-image",
      decodeMode: "image-derived",
      confidence: 0.35,
      unit: "celsius",
      note: isJpeg || isBmp
        ? "Image thermique .BMT reconnue sans matrice ToFo. Les temperatures sont derivees de la luminance et ne sont pas radiometriques."
        : "Image importee. Les temperatures sont derivees de la luminance et ne sont pas radiometriques.",
      file: meta
    };
  } catch {
    return null;
  }
};

type ScaleCandidate = {
  id: string;
  decode: (raw: number) => number;
};

const SCALE_CANDIDATES: ScaleCandidate[] = [
  { id: "kelvin_x100_le", decode: (raw) => raw / 100 - 273.15 },
  { id: "kelvin_x10_le", decode: (raw) => raw / 10 - 273.15 },
  { id: "celsius_x100_le", decode: (raw) => raw / 100 },
  { id: "celsius_x10_le", decode: (raw) => raw / 10 },
  { id: "celsius_x1_le", decode: (raw) => raw }
];

const parseBinaryRadiometric = (
  buffer: ArrayBuffer,
  file: FileMeta
): Thermogram | null => {
  const view = new DataView(buffer);
  const bytes = view.byteLength;
  let best:
    | {
        width: number;
        height: number;
        offset: number;
        signed: boolean;
        littleEndian: boolean;
        scale: ScaleCandidate;
        score: number;
      }
    | undefined;

  for (const [width, height] of COMMON_THERMAL_SIZES) {
    const count = width * height;
    const required = count * 2;
    if (bytes < required) continue;
    const offsets = candidateOffsets(bytes, required);
    for (const offset of offsets) {
      for (const signed of [false, true]) {
        for (const littleEndian of [true, false]) {
          for (const scale of SCALE_CANDIDATES) {
            const score = scoreBinaryCandidate(
              view,
              offset,
              count,
              signed,
              littleEndian,
              scale
            );
            if (!best || score > best.score) {
              best = { width, height, offset, signed, littleEndian, scale, score };
            }
          }
        }
      }
    }
  }

  if (!best || best.score < 58) return null;

  const count = best.width * best.height;
  const values = new Float32Array(count);
  let rawMin = Number.POSITIVE_INFINITY;
  let rawMax = Number.NEGATIVE_INFINITY;
  for (let index = 0; index < count; index += 1) {
    const rawOffset = best.offset + index * 2;
    const raw = best.signed
      ? view.getInt16(rawOffset, best.littleEndian)
      : view.getUint16(rawOffset, best.littleEndian);
    rawMin = Math.min(rawMin, raw);
    rawMax = Math.max(rawMax, raw);
    values[index] = best.scale.decode(raw);
  }

  return {
    width: best.width,
    height: best.height,
    values,
    decodeMode: "binary-radiometric",
    confidence: Math.min(0.82, Math.max(0.45, best.score / 100)),
    unit: "celsius",
    note: "Matrice binaire plausible detectee automatiquement. A confirmer avec un fichier de reference fabricant.",
    file,
    rawRange: {
      min: rawMin,
      max: rawMax,
      scale: `${best.scale.id}${best.signed ? "_signed" : "_unsigned"}${
        best.littleEndian ? "" : "_be"
      }`,
      offset: best.offset
    }
  };
};

const candidateOffsets = (byteLength: number, required: number) => {
  const offsets = new Set<number>([0, 16, 32, 64, 128, 256, 512, 1024, 2048, 4096]);
  const trailing = byteLength - required;
  if (trailing >= 0) offsets.add(trailing);
  for (let offset = 0; offset <= Math.min(8192, trailing); offset += 512) {
    offsets.add(offset);
  }
  return [...offsets].filter((offset) => offset >= 0 && offset + required <= byteLength);
};

const scoreBinaryCandidate = (
  view: DataView,
  offset: number,
  count: number,
  signed: boolean,
  littleEndian: boolean,
  scale: ScaleCandidate
) => {
  const sampleCount = Math.min(2400, count);
  const stride = Math.max(1, Math.floor(count / sampleCount));
  const values: number[] = [];
  for (let index = 0; index < count; index += stride) {
    const rawOffset = offset + index * 2;
    const raw = signed
      ? view.getInt16(rawOffset, littleEndian)
      : view.getUint16(rawOffset, littleEndian);
    const value = scale.decode(raw);
    if (Number.isFinite(value)) values.push(value);
  }

  if (values.length < 64) return 0;
  values.sort((a, b) => a - b);
  const p01 = values[Math.floor(values.length * 0.01)];
  const p10 = values[Math.floor(values.length * 0.1)];
  const p50 = values[Math.floor(values.length * 0.5)];
  const p90 = values[Math.floor(values.length * 0.9)];
  const p99 = values[Math.floor(values.length * 0.99)];
  const spread = p99 - p01;
  if (spread < 0.15 || p01 < -90 || p99 > 650 || p50 < -55 || p50 > 190) {
    return 0;
  }
  let score = 68;
  score -= Math.abs(p50 - 34) * 0.35;
  score -= Math.max(0, spread - 120) * 0.25;
  score += p10 > -30 && p90 < 160 ? 12 : -20;
  score += spread > 1.5 && spread < 95 ? 10 : -8;
  score += count >= 160 * 120 ? 6 : 0;
  return Math.max(0, score);
};

const parseBinaryPreview = (buffer: ArrayBuffer, file: FileMeta): Thermogram => {
  const bytes = new Uint8Array(buffer);
  const width = bytes.length >= 160 * 120 ? 160 : 96;
  const height = Math.max(48, Math.min(160, Math.ceil(bytes.length / width)));
  const values = new Float32Array(width * height);
  for (let index = 0; index < values.length; index += 1) {
    const byte = bytes[index % Math.max(1, bytes.length)] ?? 0;
    const next = bytes[(index * 7 + 13) % Math.max(1, bytes.length)] ?? byte;
    values[index] = 18 + ((byte * 0.7 + next * 0.3) / 255) * 58;
  }
  return {
    width,
    height,
    values,
    decodeMode: "binary-preview",
    confidence: 0.12,
    unit: "celsius",
    note: "Apercu binaire non radiometrique. Le fichier n'a pas encore ete decode comme matrice de temperature.",
    file
  };
};

export const formatTemp = (value: number) => `${value.toFixed(1)} °C`;

export const decodeModeLabel = (mode: DecodeMode) => {
  const labels: Record<DecodeMode, string> = {
    synthetic: "Demo",
    "tofo-radiometric": "ToFo",
    "text-matrix": "Matrice",
    "image-derived": "Image",
    "binary-radiometric": "Binaire",
    "binary-preview": "Apercu"
  };
  return labels[mode];
};
